import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, Calendar, Search, ChevronLeft, ChevronRight, MoreHorizontal, Eye, Ban, Trash2 } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { Link } from 'react-router-dom';
import { numberWithCommas } from '../utils';

const AdminUsersDashboard = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [viewMode, setViewMode] = useState('all');

  useEffect(() => {
    setLoading(true);
    socket.emit('getUsersAdmin', {
      username_like: searchTerm,
      page: currentPage,
      viewMode: viewMode
    });
  }, [viewMode]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = () => {
    setLoading(true);
    socket.emit('getUsersAdmin', {
      username_like: searchTerm,
      page: currentPage,
      viewMode: viewMode
    });

    socket.on('response_get_admin_users', (data) => {
      setUsers(data.users);
      setStats(data.stats);
      setPagination(data.pagination);
      setLoading(false);
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const statColorMap = {
    blue: '#58a6ff',
    green: '#3fb950',
    red: '#f85149',
    orange: '#f97316',
    yellow: '#d29922',
    gray: '#8b949e',
    purple: '#a855f7',
    teal: '#14b8a6'
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div style={{
      background: '#161b22',
      border: '1px solid #21262d',
      borderLeft: `3px solid ${statColorMap[color] || '#58a6ff'}`,
      borderRadius: '12px',
      padding: '12px',
      minWidth: '140px'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ padding: '4px', borderRadius: '8px' }}>
            <Icon style={{ height: '20px', width: '20px', color: statColorMap[color] || '#58a6ff' }} />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e6edf3' }}>
            {numberWithCommas(value)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '500', color: '#c9d1d9' }}>{title}</div>
          {subtitle && <p style={{ fontSize: '10px', color: '#484f58', marginTop: '4px' }}>{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const PaginationButton = ({ page, active, onClick, disabled, children }) => (
    <button
      onClick={() => onClick(page)}
      disabled={disabled}
      style={{
        padding: '8px 12px',
        fontSize: '14px',
        fontWeight: '500',
        borderRadius: '6px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? '#238636' : '#0d1117',
        color: active ? '#ffffff' : disabled ? '#484f58' : '#c9d1d9',
        transition: 'background 0.2s'
      }}
    >
      {children || page}
    </button>
  );

  const renderPagination = () => {
    const { currentPage, totalPages } = pagination;
    const pages = [];

    if (totalPages > 1) pages.push(1);

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#c9d1d9' }}>
          <span>
            Showing {((currentPage - 1) * pagination.usersPerPage) + 1} to{' '}
            {Math.min(currentPage * pagination.usersPerPage, pagination.totalUsers)} of{' '}
            {pagination.totalUsers} results
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <PaginationButton
            page={currentPage - 1}
            onClick={handlePageChange}
            disabled={currentPage === 1}
          >
            <ChevronLeft style={{ height: '16px', width: '16px' }} />
          </PaginationButton>

          {pages.map((page, index) => (
            <React.Fragment key={page}>
              {index > 0 && pages[index - 1] !== page - 1 && (
                <span style={{ padding: '0 8px', color: '#484f58' }}>...</span>
              )}
              <PaginationButton
                page={page}
                active={page === currentPage}
                onClick={handlePageChange}
              />
            </React.Fragment>
          ))}

          <PaginationButton
            page={currentPage + 1}
            onClick={handlePageChange}
            disabled={currentPage === totalPages}
          >
            <ChevronRight style={{ height: '16px', width: '16px' }} />
          </PaginationButton>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3', padding: '24px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#e6edf3' }}>User Management</h1>
          <p style={{ marginTop: '8px', color: '#c9d1d9' }}>Manage and monitor user accounts across your platform</p>
        </div>

        {/* Stats Grid */}
        <div style={{ overflowX: 'auto', width: '100%', display: 'flex', marginBottom: '16px', gap: '16px' }}>
          <StatCard
            icon={Users}
            title="Total Users"
            value={stats.totalUsers}
            color="blue"
          />
          <StatCard
            icon={UserCheck}
            title="New Yesterday"
            value={stats.newUsersYesterday}
            color="green"
          />
          <StatCard
            icon={UserX}
            title="Banned Users"
            value={stats.bannedUsers}
            color="red"
          />
          <StatCard
            icon={UserX}
            title="Banned Today"
            value={stats.bannedToday}
            color="orange"
          />
          <StatCard
            icon={UserX}
            title="Banned Yesterday"
            value={stats.bannedYesterday}
            color="yellow"
          />
          <StatCard
            icon={Trash2}
            title="Deleted Users"
            value={stats.deletedUsers}
            color="gray"
          />
        </div>

        {/* View Mode Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto' }}>
          {[
            { key: 'all', label: 'All Users' },
            { key: 'today', label: 'Joined Today' },
            { key: 'yesterday', label: 'Joined Yesterday' },
            { key: 'topBalance', label: 'Top Balances' }
          ].map(mode => (
            <button
              key={mode.key}
              onClick={() => setViewMode(mode.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: viewMode === mode.key ? '#238636' : 'rgba(33,38,45,0.5)',
                color: viewMode === mode.key ? '#ffffff' : '#c9d1d9',
                boxShadow: viewMode === mode.key ? '0 4px 12px rgba(35,134,54,0.3)' : 'none'
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {/* Main Content Card */}
        <div style={{
          background: '#161b22',
          border: '1px solid #21262d',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Search and Filters */}
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ position: 'relative', flex: '1', maxWidth: '448px' }}>
                <Search style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '16px',
                  width: '16px',
                  color: '#8b949e'
                }} />
                <input
                  type="text"
                  placeholder="Search by username or ID..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    paddingLeft: '40px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    width: '100%',
                    background: '#0d1117',
                    border: '1px solid #30363d',
                    color: '#e6edf3',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
                  onBlur={(e) => e.target.style.borderColor = '#30363d'}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {selectedUsers.length > 0 && (
                  <span style={{ fontSize: '14px', color: '#8b949e' }}>
                    {selectedUsers.length} selected
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#161b22', borderBottom: '1px solid #21262d' }}>
                  {['User', 'Country', 'Balance', 'Total Earned', 'Join Date', 'Status', 'Actions'].map(header => (
                    <th key={header} style={{
                      padding: '12px 24px',
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#8b949e',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px'
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} style={{ padding: '12px' }}>
                          <div style={{
                            height: '16px',
                            background: '#21262d',
                            borderRadius: '4px',
                            animation: 'pulse 2s infinite',
                            width: j === 0 ? '96px' : j === 6 ? '32px' : '64px'
                          }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: '1px solid #21262d',
                        transition: 'background 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(33,38,45,0.5)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#e6edf3' }}>
                            ({user.id}) {user.username}
                          </div>
                          {user.email && (
                            <div style={{ fontSize: '14px', color: '#484f58' }}>{user.email}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#c9d1d9' }}>
                        {user.country}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#3fb950', fontWeight: '600' }}>
                        ${user.balance}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#c9d1d9' }}>
                        ${user.total_earned}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#8b949e' }}>
                        {user.join_date}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {user.banned === 'Yes' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: 'rgba(248,81,73,0.15)',
                              color: '#f85149'
                            }}>
                              Banned
                            </span>
                          )}
                          {user.deleted === 'Yes' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: 'rgba(139,148,158,0.15)',
                              color: '#8b949e'
                            }}>
                              Deleted
                            </span>
                          )}
                          {user.banned === 'No' && user.deleted === 'No' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: 'rgba(63,185,80,0.15)',
                              color: '#3fb950'
                            }}>
                              Active
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Link to={`/pluto/user/${user.id}`} style={{ color: '#3fb950', transition: 'color 0.2s' }}>
                            <Eye style={{ height: '20px', width: '20px' }} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && renderPagination()}
        </div>
      </div>

      {/* Pulse animation for skeleton loading */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default AdminUsersDashboard;
